import React, { createContext, useContext, useRef, useEffect, useCallback, useState } from "react";
import Shepherd from "shepherd.js";
import { useLocation } from "react-router-dom";
import { useAuth } from "./authContext";
import { useOptions } from "./optionContext";
import { markPageTourCompleted as markPageTourCompletedService } from "../services/userService";
import { useTranslation } from "react-i18next";
import "shepherd.js/dist/css/shepherd.css";

import dashboardTour from "../pages/Tours/dashboardTour.json"
import rsplTour from "../pages/Tours/rsplTour.json"
import cartTour from "../pages/Tours/cartTour.json"
import machineTour from "../pages/Tours/machineTour.json"
import productTour from "../pages/Tours/productTour.json"
import analyticsTour from "../pages/Tours/analyticsTour.json"
import checkoutTour from "../pages/Tours/checkoutTour.json"
import adminTour from "../pages/Tours/adminTour.json"
import requestTour from "../pages/Tours/requestTour.json"
import recommendationTour from "../pages/Tours/recommendationTour.json"
import SFLTour from "../pages/Tours/SFLTour.json"
import orderHistoryTour from "../pages/Tours/orderHistoryTour.json"
import profileTour from "../pages/Tours/profileTour.json"

const TourContext = createContext();

const normalizeRoute = (pathname) => {
    let route = pathname.startsWith('/') ? pathname.slice(1) : pathname;
    
    if (route.match(/^machines\/\d+$/)) {
        return 'machines/:id';
    }
    
    if (route === '' || route === 'dashboard') {
        return 'home';
    }
    
    return route;
};

export const TourProvider = ({ children }) => {
    const tourRef = useRef(null);
    const [isTourActive, setIsTourActive] = useState(false);
    const [hasAutoStarted, setHasAutoStarted] = useState(false);
    const { user, updateUserStatus } = useAuth();
    const location = useLocation();
    const { t } = useTranslation();
    const { optionKeys } = useOptions();
    const toursEnabled = optionKeys?.enableTours !== "false";

    const userId = user?.user_id;
    const permissions = user?.role?.permissions;
    const hasSeenTour = user?.has_seen_tour || false;
    const completedTours = user?.completed_tours || {};
    
    const hasUserManagement = permissions?.user_management?.edit_users || 
                              permissions?.user_management?.create_users || 
                              permissions?.user_management?.delete_users;
    
    const hasRoleManagement = permissions?.role_management?.edit_roles || 
                              permissions?.role_management?.create_roles || 
                              permissions?.role_management?.delete_roles;
    
    const hasOrderManagement = permissions?.order_management?.create_order || 
                               permissions?.order_management?.edit_order_schedules;
    
    const hasMachineProvision = true;
    
    const hasAnalytics = permissions?.analytics === true;

    const hasRequestsManagement = permissions?.requests_management?.view_request_history ||
                              permissions?.requests_management?.allow_notify_customer_care;

    const hasRecommendationsManagement = true;

    const hasSFL = true;

    const hasOH = true;

    const hasProfile = true;

    const getStepsForRoute = useCallback(() => {
        const route = location.pathname;
        
        const getStepsByPermissions = (tourConfig) => {
            if (route.startsWith("/admin")) {
                return hasUserManagement ? tourConfig.userSteps : null;
            }
            
            if (route.startsWith("/analytics")) {
                return hasAnalytics ? tourConfig.userSteps : null;
            }
            
            if (route.startsWith("/checkout") || route.startsWith("/cart")) {
                return hasOrderManagement ? tourConfig.userSteps : null;
            }
            
            if (route.startsWith("/machines")) {
                return hasMachineProvision ? tourConfig.userSteps : null;
            }
            if (route.startsWith("/requests")) {
                return hasRequestsManagement ? tourConfig.userSteps : tourConfig.userSteps;
            }
            if (route.startsWith("/recommendations")) {
                return hasRecommendationsManagement ? tourConfig.userSteps : tourConfig.userSteps;
            }
            if (route.startsWith("/saved_for_later")) {
                return hasSFL ? tourConfig.userSteps : tourConfig.userSteps;
            }
            if (route.startsWith("/order-history")) {
                return hasOH ? tourConfig.userSteps : tourConfig.userSteps;
            }
            if (route.startsWith("/profile")) {
                return hasProfile ? tourConfig.userSteps : tourConfig.userSteps;
            }
            return tourConfig.userSteps;
        };
        
        if (route === "/" || route.startsWith("/dashboard") || route.startsWith("/home")) {
            return getStepsByPermissions(dashboardTour);
        }
        if (route.startsWith("/rspl")) {
            return getStepsByPermissions(rsplTour);
        }
        if (route.startsWith("/cart")) {
            return getStepsByPermissions(cartTour);
        }
        if (route.match(/^\/machines\/\d+$/)) {
            return getStepsByPermissions(productTour);
        }
        if (route.startsWith("/machines")) {
            return getStepsByPermissions(machineTour);
        }
        if (route.startsWith("/analytics")) {
            return getStepsByPermissions(analyticsTour);
        }
        if (route.startsWith("/checkout")) {
            return getStepsByPermissions(checkoutTour);
        }
        if (route.startsWith("/admin")) {
            return getStepsByPermissions(adminTour);
        }
        if (route.startsWith("/requests")) {
            return getStepsByPermissions(requestTour);
        }
        if (route.startsWith("/recommendations")) {
            return getStepsByPermissions(recommendationTour);
        }
        if (route.startsWith("/saved_for_later")) {
            return getStepsByPermissions(SFLTour);
        }
        if (route.startsWith("/order-history")) {
            return getStepsByPermissions(orderHistoryTour);
        }
        if (route.startsWith("/profile")) {
            return getStepsByPermissions(profileTour);
        }
        return null;
    }, [location.pathname, hasUserManagement, hasRoleManagement, hasOrderManagement, hasMachineProvision, hasAnalytics, hasRequestsManagement, hasRecommendationsManagement, hasSFL, hasOH, hasProfile]);

    const hasCompletedCurrentPageTour = useCallback(() => {
        if (!userId) return false;
        const normalizedRoute = normalizeRoute(location.pathname);
        return completedTours[normalizedRoute] === true;
    }, [location.pathname, completedTours, userId]);

    const markPageTourAsCompleted = useCallback(async () => {
        if (!userId) {
            console.warn("Cannot mark tour as completed: no user logged in");
            return;
        }
        
        try {
            const normalizedRoute = normalizeRoute(location.pathname);
            
            
            const response = await markPageTourCompletedService(userId, normalizedRoute);
            
            if (response.status) {
                const updatedCompletedTours = {
                    ...completedTours,
                    [normalizedRoute]: true
                };
                
                await updateUserStatus({ 
                    has_seen_tour: true,
                    completed_tours: updatedCompletedTours 
                });
                
                
            } else {
                console.error("Failed to mark page tour as completed:", response);
            }
        } catch (error) {
            console.error('Failed to update page tour status:', error);
        }
    }, [userId, location.pathname, completedTours, updateUserStatus]);

    const createTour = useCallback((steps) => {
        if (tourRef.current) {
            tourRef.current.complete();
            tourRef.current = null;
        }

        const cleanupTourStyles = () => {
            const tourButton = document.querySelector("[data-tour='tour-button']");
            if (tourButton) {
                tourButton.style.position = 'fixed';
                tourButton.style.bottom = '90px';
                tourButton.style.right = '30px';
                tourButton.style.zIndex = '99999';
            }

            const footer = document.querySelector("[data-tour='main-page-footer']");
            if (footer) {
                footer.style.position = '';
                footer.style.zIndex = '';
            }
        };

        const tour = new Shepherd.Tour({
            useModalOverlay: true,
            defaultStepOptions: {
                scrollTo: { behavior: "smooth", block: "center" },
                cancelIcon: { enabled: true },
                classes: "shepherd-theme-custom shadow-md bg-white rounded-lg",
                when: {
                    show() {
                        const currentStep = tour.getCurrentStep();
                        if (currentStep) {
                            const element = document.querySelector(currentStep.options.attachTo?.element);
                            if (!element) {
                                console.warn(`Tour element not found: ${currentStep.options.attachTo?.element}`);
                            }
                        }
                    }
                }
            },
        });

        steps.forEach((step) => {
            const buttons = [
                {
                    text: t('BUTTONS.CANCEL'),
                    classes: "shepherd-button-secondary",
                    action: () => tour.cancel(),
                },
                ...step.buttons.map((btn) => {
                    const buttonConfig = {
                        text: btn.text === t('BUTTON.NEXT') ? t('BUTTONS.NEXT') : 
                              btn.text === t('BUTTON.BACK') ? t('BUTTONS.BACK') : 
                              btn.text === t('BUTTON.FINISH') ? t('BUTTONS.FINISH') : btn.text,
                        classes:
                            btn.action === t('BUTTON.NEXT') || btn.action === "complete"
                                ? "shepherd-button-primary"
                                : "shepherd-button-secondary",
                    };

                    if (btn.action === "next") {
                        buttonConfig.action = () => tour.next();
                    } else if (btn.action === "back") {
                        buttonConfig.action = () => tour.back();
                    } else if (btn.action === "complete") {
                        buttonConfig.action = () => tour.complete();
                    } else if (typeof btn.action === "function") {
                        buttonConfig.action = btn.action;
                    }

                    return buttonConfig;
                }),

                //   Injected Cancel button (no JSON change)

            ];


            tour.addStep({
                id: step.id,
                title: step.title.startsWith('TOURS.') ? t(step.title) : step.title,
                text: step.text.startsWith('TOURS.') ? t(step.text) : step.text,
                attachTo: {
                    element: step.attachTo?.element,
                    on: step.attachTo?.on || "bottom"
                },
                buttons: buttons,
                scrollTo: step.attachTo?.element === "[data-tour='admin-menu']" ? false : undefined,
                modalOverlayOpeningPadding: step.id === 'main-page-footer' ? 15 : 10,
                modalOverlayOpeningRadius: step.id === 'main-page-footer' ? 8 : 4,
                when: {
                    show() {
                        const element = document.querySelector(step.attachTo?.element);

                        if (element) {
                            if (step.attachTo?.element === "[data-tour='admin-menu']") {
                                // No scroll for admin menu
                            } else if (step.id === 'tour-button-intro') {
                                element.style.position = 'fixed';
                                element.style.bottom = '90px';
                                element.style.right = '30px';
                                element.style.zIndex = '99999';
                            } else if (step.id === 'main-page-footer') {
                                window.scrollTo({
                                    top: document.documentElement.scrollHeight,
                                    behavior: 'smooth'
                                });
                                
                                setTimeout(() => {
                                    element.style.position = 'relative';
                                    element.style.zIndex = '9999';
                                    
                                    const tour = this.tour;
                                    if (tour && tour.getCurrentStep()) {
                                        tour.getCurrentStep().updateStepOptions({
                                            attachTo: step.attachTo
                                        });
                                    }
                                }, 400);
                            } else {
                                element.scrollIntoView({ behavior: "smooth", block: "center" });
                            }
                        } else {
                            console.warn(`Tour element not found: ${step.attachTo?.element}`);
                        }
                    },
                    hide() {
                        const element = document.querySelector(step.attachTo?.element);
                        if (element) {
                            if (step.id === 'tour-button-intro') {
                                element.style.position = 'fixed';
                                element.style.bottom = '90px';
                                element.style.right = '30px';
                                element.style.zIndex = '99999';
                            } else if (step.id === 'main-page-footer') {
                                element.style.position = '';
                                element.style.zIndex = '';
                            }
                        }
                    }
                }
            });
        });

        const finishTour = async () => {
            cleanupTourStyles();
            setIsTourActive(false);
            await markPageTourAsCompleted();
        };

        tour.on("complete", finishTour);
        tour.on("cancel", finishTour);

        tour.on("start", () => {
            setIsTourActive(true);
        });

        tourRef.current = tour;
        return tour;
    }, [markPageTourAsCompleted]);

    const startTour = useCallback(() => {
        if (!toursEnabled) return;
        const steps = getStepsForRoute();
        if (!steps?.length) {
            console.warn("No tour steps available for this route");
            return;
        }

        const tour = createTour(steps);
        
        setTimeout(() => {
            if (tour) {
                tour.start();
            }
        }, 500);
    }, [getStepsForRoute, createTour, toursEnabled]);

    const cancelTour = useCallback(() => {
        if (tourRef.current) {
            tourRef.current.cancel();
        }
        setIsTourActive(false);
    }, []);

    const resetTour = useCallback(async () => {
        if (!userId) {
            console.warn("Cannot reset tour: no user logged in");
            return;
        }
        
        try {
            const normalizedRoute = normalizeRoute(location.pathname);
            
            const updatedCompletedTours = { ...completedTours };
            delete updatedCompletedTours[normalizedRoute];
            
            await updateUserStatus({ 
                completed_tours: updatedCompletedTours 
            });
            
            if (tourRef.current) {
                tourRef.current.complete();
            }
            setIsTourActive(false);
            
            
        } catch (error) {
            console.error("Failed to reset tour:", error);
        }
    }, [location.pathname, userId, completedTours, updateUserStatus]);

    const hasTourSteps = useCallback(() => {
        if (!toursEnabled) return false;
        const steps = getStepsForRoute();
        return !!(steps && steps.length > 0);
    }, [getStepsForRoute, toursEnabled]);

    const isTourCompleted = useCallback(() => {
        return hasCompletedCurrentPageTour();
    }, [hasCompletedCurrentPageTour]);

    useEffect(() => {
        if (tourRef.current) {
            tourRef.current.complete();
            tourRef.current = null;
        }
        setIsTourActive(false);
        setHasAutoStarted(false);
        
        
    }, [userId]);

    useEffect(() => {
        if (toursEnabled) return;
        if (tourRef.current) {
            tourRef.current.cancel();
            tourRef.current = null;
        }
        setIsTourActive(false);
        setHasAutoStarted(false);
    }, [toursEnabled]);

    useEffect(() => {
        if (!toursEnabled) return;
        if (!userId || hasAutoStarted || hasSeenTour) return;

        const steps = getStepsForRoute();
        if (!steps?.length) return;

        const hasCompletedThisPage = hasCompletedCurrentPageTour();
        
        if (!hasCompletedThisPage) {
            const timer = setTimeout(() => {
                
                startTour();
                setHasAutoStarted(true);
            }, 1500);

            return () => clearTimeout(timer);
        }
    }, [location.pathname, userId, getStepsForRoute, startTour, hasAutoStarted, hasCompletedCurrentPageTour, hasSeenTour, toursEnabled]);

    useEffect(() => {
        return () => {
            if (tourRef.current) {
                tourRef.current.complete();
            }
        };
    }, [location.pathname]);

    useEffect(() => {
        setHasAutoStarted(false);
    }, [location.pathname]);

    return (
        <TourContext.Provider value={{ 
            startTour, 
            cancelTour, 
            resetTour,
            hasTourSteps,
            isTourCompleted,
            isTourActive,
            permissions,
            hasUserManagement
        }}>
            {children}
        </TourContext.Provider>
    );
};

export const useTour = () => {
    const context = useContext(TourContext);
    if (!context) {
        throw new Error("useTour must be used within TourProvider");
    }
    return context;
};
